import { useState } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  buttonSecondary,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { GALLERY, LiteVideo, VIDEOS } from './kit';

const FIRST = 12;

export default function WorkshopsGallery() {
  const { ref, state } = useReveal<HTMLDivElement>(0.15);
  const [showAll, setShowAll] = useState(false);
  const photos = showAll ? GALLERY : GALLERY.slice(0, FIRST);

  return (
    <section className='pb-24 sm:pb-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='From our workshops.'
          tail='Hear it from the people who came.'
          lead='Public sessions, company teams and partners such as Google, CapitaLand and Huttons Group, since 2024.'
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-14 grid gap-4 md:grid-cols-2')}
        >
          {[VIDEOS.feedback, VIDEOS.recap].map((video, index) => (
            <div
              key={video.id}
              className={clsxm(
                styles.rv,
                'overflow-hidden rounded-[24px] shadow-[0_24px_50px_-30px_rgba(30,27,75,0.55)]',
                tone.line,
              )}
              style={delay(index * 100)}
            >
              <LiteVideo video={video} />
            </div>
          ))}
        </div>

        <ul className='mt-4 columns-2 gap-4 sm:columns-3 lg:columns-4'>
          {photos.map((photo) => (
            <li
              key={photo.src}
              className='group relative mb-4 break-inside-avoid overflow-hidden rounded-[18px] bg-[#e8e8f0] dark:bg-white/5'
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={photo.src}
                alt={`${photo.title}, ${photo.date}`}
                width={photo.width}
                height={photo.height}
                loading='lazy'
                decoding='async'
                className='block h-auto w-full transition-transform duration-500 group-hover:scale-[1.03]'
              />
              <span className='pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-3.5 pb-3 pt-10 text-[12px] font-medium leading-4 text-white opacity-100 transition-opacity duration-300 sm:opacity-0 sm:group-hover:opacity-100'>
                <span className='block truncate'>{photo.title}</span>
                <span className='text-white/70'>{photo.date}</span>
              </span>
            </li>
          ))}
        </ul>

        {GALLERY.length > FIRST ? (
          <div className='mt-6 flex justify-center'>
            <button
              type='button'
              onClick={() => setShowAll((value) => !value)}
              aria-expanded={showAll}
              className={buttonSecondary}
            >
              {showAll
                ? 'Show fewer photos'
                : `Show all ${GALLERY.length} photos`}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
